import type { Check, Dokument, ExtrahierteFelder } from "../api/typen";
import { datum, kwp } from "../lib/format";
import { AMPEL_KLASSEN, ampel } from "../lib/konfidenz";
import { formatiereIban } from "../lib/validierung";
import { Hinweis } from "./Basis";
import { AmpelPunkt, KonfidenzAnzeige } from "./Konfidenz";

const ANZEIGE: { feld: keyof ExtrahierteFelder; label: string; format?: (w: never) => string }[] = [
  { feld: "leistung_kwp", label: "Leistung", format: (w: number) => kwp(w) },
  { feld: "modultyp", label: "Modultyp" },
  { feld: "wechselrichter", label: "Wechselrichter" },
  { feld: "strasse", label: "Straße" },
  { feld: "plz", label: "PLZ" },
  { feld: "ort", label: "Ort" },
  { feld: "zaehlernummer", label: "Zählernummer" },
  { feld: "iban", label: "IBAN", format: (w: string) => formatiereIban(w) },
  { feld: "zerez_id", label: "ZEREZ-ID" },
  { feld: "anzahl_module", label: "Anzahl Module" },
  { feld: "modulleistung_wp", label: "Modulleistung", format: (w: number) => `${w} Wp` },
  { feld: "wechselrichterleistung_kw", label: "WR-Leistung", format: (w: number) => `${String(w).replace(".", ",")} kW` },
  { feld: "speicher_vorhanden", label: "Speicher", format: (w: boolean) => (w ? "Ja" : "Nein") },
  { feld: "speicher_kapazitaet_kwh", label: "Speicherkapazität", format: (w: number) => `${String(w).replace(".", ",")} kWh` },
  { feld: "inbetriebnahme_datum", label: "Inbetriebnahme", format: (w: string) => datum(w) },
];

const VOLLMACHT_ANZEIGE: typeof ANZEIGE = [
  { feld: "unterschrift_vorhanden", label: "Unterschrift (Claude)", format: (w: boolean) => (w ? "Ja" : "Nein") },
  { feld: "vollmacht_datum", label: "Datum", format: (w: string) => datum(w) },
  { feld: "vollmacht_umfang", label: "Umfang" },
];

/** Nur-Lese-Ansicht der extrahierten Felder eines Dokuments, farbcodiert nach Konfidenz. */
export function FeldListe({ dokument }: { dokument: Dokument }) {
  const daten = dokument.extrahierte_daten;
  if (!daten) return null;
  const konfidenz = dokument.konfidenz ?? {};
  // Ältere Extraktionen kennen die MaStR-Felder noch nicht – die dann nicht als „nicht erkannt“ zeigen.
  const bekannt = ANZEIGE.filter((f) => f.feld in daten);
  const felder = dokument.typ === "vollmacht" ? [...VOLLMACHT_ANZEIGE, ...bekannt.filter((f) => daten[f.feld] !== null)] : bekannt;
  const warnungen = new Map(dokument.warnungen.map((w) => [w.feld, w.meldung]));

  return (
    <dl className="divide-y divide-neutral-100 rounded-xl border border-neutral-200">
      {felder.map(({ feld, label, format }) => {
        const wert = daten[feld];
        const hatWert = wert !== null && wert !== undefined;
        const stufe = ampel(konfidenz[feld], hatWert);
        return (
          <div key={feld} className={`grid grid-cols-[8.5rem_1fr_auto] items-start gap-3 border-l-4 px-3 py-2.5 ${AMPEL_KLASSEN[stufe].rahmen}`}>
            <dt className="text-xs font-semibold text-neutral-500">{label}</dt>
            <dd className="min-w-0 break-words text-sm">
              {hatWert ? (format ? (format as (w: unknown) => string)(wert) : String(wert)) : <span className="italic text-neutral-400">nicht erkannt</span>}
              {warnungen.get(feld) && <span className="mt-0.5 block text-xs text-ampel-rot">⚠ {warnungen.get(feld)}</span>}
            </dd>
            <dd>
              <KonfidenzAnzeige konfidenz={konfidenz[feld]} hatWert={hatWert} />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function CheckZeile({ titel, check }: { titel: { ja: string; nein: string; offen: string }; check: Check }) {
  // Ein negatives Ergebnis ist immer ein Problem (rot), egal wie sicher es erkannt wurde.
  const stufe = check.wert === true ? ampel(check.konfidenz) : "rot";
  const symbol = check.wert === null ? "?" : check.wert ? "✓" : "✗";
  const text = check.wert === null ? titel.offen : check.wert ? titel.ja : titel.nein;
  return (
    <li className={`flex items-start gap-3 rounded-lg px-3 py-2 ${AMPEL_KLASSEN[stufe].hintergrund}`}>
      <AmpelPunkt stufe={stufe} gross />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {symbol} {text}
          <span className="ml-2 text-xs font-normal text-neutral-500" title="Sicherheit der Prüfung">
            Sicherheit {Math.round(check.konfidenz * 100)} %
          </span>
        </p>
        <p className="text-xs text-neutral-600">{check.details}</p>
      </div>
    </li>
  );
}

export function VollmachtChecks({ dokument }: { dokument: Dokument }) {
  const p = dokument.pruefungen;
  if (!p) return null;
  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        <CheckZeile
          titel={{ ja: "Unterschrift erkannt", nein: "Keine Unterschrift erkannt", offen: "Unterschrift nicht prüfbar" }}
          check={p.unterschrift}
        />
        <CheckZeile titel={{ ja: "Datum im Dokument", nein: "Kein Datum gefunden", offen: "Datum nicht prüfbar" }} check={p.datum} />
        <CheckZeile
          titel={{ ja: "MaStR / Netzanmeldung erwähnt", nein: "MaStR / Netzanmeldung nicht erwähnt", offen: "Umfang nicht prüfbar" }}
          check={p.umfang}
        />
      </ul>
      <p className="text-xs text-neutral-500">Pixel-Analyse der Unterschrift: {p.unterschrift_pixel.details}</p>
      {dokument.hinweis && <Hinweis art="warnung">📷 {dokument.hinweis}</Hinweis>}
    </div>
  );
}

export function ExtraktionsZusammenfassung({ dokument }: { dokument: Dokument }) {
  const k = dokument.konfidenz ?? {};
  const daten = dokument.extrahierte_daten;
  if (!daten) return null;
  const werte = Object.entries(daten).filter(([f]) => f !== "vollmacht_umfang");
  const erkannt = werte.filter(([, w]) => w !== null).length;
  const unsicher = werte.filter(([f, w]) => w !== null && (k[f] ?? 0) < 0.5).length;
  const pruefen = werte.filter(([f, w]) => w !== null && (k[f] ?? 0) >= 0.5 && (k[f] ?? 0) <= 0.8).length;
  return (
    <p className="text-xs text-neutral-600">
      {erkannt} Felder erkannt
      {pruefen > 0 && <span className="text-ampel-gelb"> · {pruefen} prüfen</span>}
      {unsicher > 0 && <span className="text-ampel-rot"> · {unsicher} unsicher</span>}
    </p>
  );
}
